import { history as historyRouter } from 'instantsearch.js/es/lib/routers';
import type { UiState } from 'instantsearch.js';
import { algoliaAttributes } from 'assets/constants';
import cloneDeep from 'lodash/cloneDeep';

type RouteState = {
  page?: string;
  workday?: string[];
  times?: string[];
  city?: string[];
  cityCode?: string[];
  medicalServices?: string[];
  name?: string[];
  sortBy?: string;
  hitsPerPage?: string;
  loc?: string | undefined | null;
  r?: number | "all" | undefined;
};

const routeStateDefaultValues: RouteState = {
  page: '1',
  workday: undefined,
  times: undefined,
  city: undefined,
  cityCode: undefined,
  medicalServices: undefined,
  name: undefined,
  sortBy: 'instant_search',
  hitsPerPage: '20',
  loc: undefined,
  r: 20000,
};

const router = historyRouter<RouteState>({

  createURL({ qsModule, routeState, location }): string {
    const { protocol, hostname, port = '', pathname, hash } = location;
    const portWithPrefix = port === '' ? '' : `:${port}`;
    const baseUrl = `${protocol}//${hostname}${portWithPrefix}${pathname}`;
    const currentQuery = qsModule.parse(location.search.slice(1)) as Partial<RouteState>;


    const queryParameters: Partial<RouteState> = {};

// keep existing loc/r from URL if routeState didn't provide them this time
const existingLoc = currentQuery.loc;
const existingR = currentQuery.r;

const existingRNum =
  typeof existingR === 'string' ? parseInt(existingR, 10) :
  typeof existingR === 'number' ? existingR :
  undefined;

// Prefer loc/r from routeState, but fallback to existing URL params
const finalLoc = routeState.loc ?? existingLoc;
const finalRNum =
  typeof routeState.r === 'number' ? routeState.r :
  existingRNum ?? 20000;


    if (
      routeState.workday &&
      routeState.workday !== routeStateDefaultValues.workday
    ) {
      queryParameters.workday = routeState.workday.map(encodeURIComponent);
    }

    if (routeState.name && routeState.name !== routeStateDefaultValues.name) {
      queryParameters.name = routeState.name.map(encodeURIComponent);
    }

    if (
      routeState.times &&
      routeState.times !== routeStateDefaultValues.times
    ) {
      queryParameters.times = routeState.times.map(encodeURIComponent);
    }

    if (
      routeState.city &&
      routeState.city !== routeStateDefaultValues.city
    ) {
      queryParameters.city = routeState.city.map(encodeURIComponent);
    }

    if (
      routeState.cityCode &&
      routeState.cityCode !== routeStateDefaultValues.cityCode
    ) {
      queryParameters.cityCode = routeState.cityCode;
    }
    if (
      routeState.medicalServices &&
      routeState.medicalServices !== routeStateDefaultValues.medicalServices
    ) {
      queryParameters.medicalServices = routeState.medicalServices.map(encodeURIComponent);
    }

    if (
      routeState.sortBy &&
      routeState.sortBy !== routeStateDefaultValues.sortBy
    ) {
      queryParameters.sortBy = routeState.sortBy;
    }
    if (
      routeState.hitsPerPage &&
      routeState.hitsPerPage !== routeStateDefaultValues.hitsPerPage
    ) {
      queryParameters.hitsPerPage = routeState.hitsPerPage;
    }
    if (finalLoc && finalLoc !== routeStateDefaultValues.loc) {
      queryParameters.loc = finalLoc;
      queryParameters.r = finalRNum;
    }
    
    
    const queryString = qsModule.stringify(queryParameters, {
      addQueryPrefix: true,
      arrayFormat: 'repeat',
    });

    return `${baseUrl}${queryString}${hash}`;
  },

  parseURL({ qsModule, location }): RouteState {
    const queryParameters = qsModule.parse(location.search.slice(1));
    const {
      page = 1,
      workday,
      times,
      city,
      cityCode,
      medicalServices,
      name,
      hitsPerPage,
      sortBy,
      loc,
      r
    } = queryParameters;


    const allcityCode = (
      Array.isArray(cityCode) ? cityCode : [cityCode].filter(Boolean)
    ) as string[];

    const allmedicalServicesCode = (
      Array.isArray(medicalServices) ? medicalServices : [medicalServices].filter(Boolean)
    ) as string[];

    const allName = (
      Array.isArray(name) ? name : [name].filter(Boolean)
    ) as string[];

    const allworkday = (
      Array.isArray(workday) ? workday : [workday].filter(Boolean)
    ) as string[];

    const alltimes = (
      Array.isArray(times) ? times : [times].filter(Boolean)
    ) as string[];

    const allcities = (
      Array.isArray(city) ? city : [city].filter(Boolean)
    ) as string[];

    return {
      page: page as string,
      workday: allworkday.map(decodeURIComponent),
      times: alltimes.map(decodeURIComponent),
      city: allcities.map(decodeURIComponent),
      cityCode: allcityCode.map(decodeURIComponent),
      medicalServices: allmedicalServicesCode.map(decodeURIComponent),
      name: allName.map(decodeURIComponent),
      sortBy: sortBy as string,
      hitsPerPage: hitsPerPage as string,
      loc: typeof loc === 'string' ? decodeURIComponent(loc) : undefined,
      r: r ? parseInt(r as string) : 20000,
    };
  },
});

const getStateMapping = ({ indexName, objectType }: { indexName: string, objectType: any }) => ({
  stateToRoute(uiState: UiState): RouteState {
    const indexUiState = uiState[indexName];
    const exactUiState = uiState['exact'];
    let aroundRadiusMatches;
    let arroundLatLngMatches;
    let cityCodesMatches;
    if (exactUiState != undefined) {
      aroundRadiusMatches = exactUiState?.configure?.aroundRadius;
      arroundLatLngMatches = exactUiState?.configure?.aroundLatLng;
      cityCodesMatches = exactUiState?.refinementList && exactUiState?.refinementList[algoliaAttributes.medicalCityCode];
    }
    else {
      aroundRadiusMatches = indexUiState?.configure?.aroundRadius;
      arroundLatLngMatches = indexUiState?.configure?.aroundLatLng;
      cityCodesMatches = indexUiState?.refinementList && indexUiState?.refinementList[algoliaAttributes.medicalCityCode];
    }

    return {
      page: (indexUiState.page && String(indexUiState.page)) || undefined,
      workday: indexUiState.refinementList && indexUiState.refinementList['workingHours.dayOfWeek'],
      times: indexUiState.refinementList && indexUiState.refinementList['workingHours.timeOfDay'],
      city: indexUiState.refinementList && indexUiState.refinementList['city'],
      cityCode: cityCodesMatches,
      medicalServices: indexUiState.refinementList && indexUiState?.refinementList['medicalServices'],
      name: indexUiState.refinementList && indexUiState.refinementList[algoliaAttributes.Name],
      sortBy: indexUiState.sortBy,
      hitsPerPage: (indexUiState.hitsPerPage && String(indexUiState.hitsPerPage)) || undefined,
      loc: arroundLatLngMatches ? arroundLatLngMatches : undefined,
      r: aroundRadiusMatches ? aroundRadiusMatches : undefined,
    };
  },

  routeToState(routeState: RouteState): UiState {

    const workdays = new URLSearchParams(window.location.search).getAll('workday').map(day => decodeURIComponent(day));
    const times = new URLSearchParams(window.location.search).getAll('times').map(time => decodeURIComponent(time));


    const refinementList: { [key: string]: string[] } = {};
    let filters = '';
    switch (objectType) {
      case 4:
        filters = 'objectType:LeumitMedicalCenter ';
        break;
      case 5:
        filters = 'objectType:Optica ';
        break;
      case 6:
        filters = 'objectType:Emergency ';
        break;
      case 7:
        filters = 'objectType:Pharmacy ';
        break;
      case 8:
        filters = 'objectType:WomenEmergency ';
        break;
      case 9:
        filters = 'objectType:Dental ';
        break;
      default:
        break;
    }
    let aroundLatLng = undefined;
    let aroundRadius = undefined;
    let configure = {};
    let geoSearch = false;
    let geofilters = '';
    let exactfilters = '';
    let geoconfigure = {};
    let exactconfigure = {};



    if (routeState.city) {
      refinementList['city'] = routeState.city;
    }

    if (routeState.medicalServices) {
      refinementList['medicalServices'] = objectType == '9' ? (routeState.medicalServices[0] ? routeState.medicalServices[0].split(',') : []) : routeState.medicalServices;
    }

    if (routeState.workday && routeState.workday.length) {
      refinementList["workingHours.dayOfWeek"] = workdays.length > 1 ? workdays : routeState.workday[0].split(',');
    }

    if (routeState.times && routeState.times.length) {

      refinementList["workingHours.timeOfDay"] = times.length > 1 ? times : routeState.times[0].split(',');
    }

    if (routeState.name && routeState.name.length) {         
      refinementList.name = routeState.name;
    }


    if (!routeState.cityCode || !routeState.cityCode.length) {
      if (routeState.loc && routeState.loc !== 'undefined') {
        geoSearch = true;
        aroundLatLng = routeState.loc;
        aroundRadius = routeState.r ?? 20000;
      } else {
        geoSearch = false;
      }
    } else {
      if (routeState.loc && routeState.loc !== 'undefined') {
        geoSearch = true;
        aroundLatLng = routeState.loc;
        aroundRadius = routeState.r;
      } else {
        geoSearch = false;
      }

      const urlParams = new URLSearchParams(window.location.search);
      let cityCodes = urlParams.getAll('cityCode');

      if (cityCodes.length === 1 && cityCodes[0].includes(',')) {
        cityCodes = cityCodes[0].split(',');
      }
      const citiesFilter = cityCodes.map((cityCode) => {
        return `cityCode:${cityCode}`;
      });
      refinementList[algoliaAttributes.medicalCityCode] = cityCodes;
      if (geoSearch) {
        exactfilters = geofilters = filters;
        exactfilters += filters ? ` AND ${citiesFilter.join(' OR ')}` : `${citiesFilter.join(' OR ')}`;
        geofilters += filters ? ` AND NOT ${citiesFilter.join(' OR ')}` : `NOT ${citiesFilter.join(' OR ')}`;
      }
      else {
        filters += filters ? ` AND (${citiesFilter.join(' OR ')})` : `(${citiesFilter.join(' OR ')})`;
      }
    }

    if (filters) {
      configure = { ...configure, filters };
    }
    if (aroundLatLng) {
      configure = { ...configure, aroundLatLng, aroundRadius };
    }
    if (geofilters) {
      geoconfigure = { ...geoconfigure, filters: geofilters };
      exactconfigure = { ...exactconfigure, filters: exactfilters };
    }
    if (aroundLatLng) {
      exactconfigure = { ...exactconfigure, aroundLatLng, aroundRadius };
      geoconfigure = { ...geoconfigure, aroundLatLng, aroundRadius, getRankingInfo: true };
    }

    if (geoSearch) {
      let modifiedRefList = cloneDeep(refinementList);
      delete modifiedRefList[algoliaAttributes.medicalCityCode];
      return {
        [indexName]: {
          page: Number(routeState.page),
          refinementList: modifiedRefList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: configure ? configure : undefined,
        },
        ['exact']: {
          page: Number(routeState.page),
          refinementList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: exactconfigure ? exactconfigure : undefined,
        },
        ['geo']: {
          page: Number(routeState.page),
          refinementList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: geoconfigure ? geoconfigure : undefined,
        },
      };
    }
    else {
      return {
        [indexName]: {
          page: Number(routeState.page),
          refinementList,
          sortBy: routeState.sortBy,
          hitsPerPage: Number(routeState.hitsPerPage),
          configure: configure ? configure : undefined,
        }
      };
    }
  },
});

const getRouting = (indexName: string, objectType: any) => ({
  router,
  stateMapping: getStateMapping({ indexName, objectType }),
});

export default getRouting;